import type { IncomingCallAlert } from 'app/types/incoming-call'
import { logWarn } from 'lib/logging'

/**
 * Per tab, because several app links are full page loads: an in-memory record would forget the
 * call on the way, and the store's catch-up would chime it again with a fresh countdown and no pin.
 */
const STORAGE_KEY = `trkIncomingCallAlerts`

/** A front desk leaves this tab open all day, so the ledger is bounded. */
const MAX_ALERTS = 200

export function readAlertLedger(): IncomingCallAlert[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? `[]`)
    return Array.isArray(parsed) ? (parsed as IncomingCallAlert[]) : []
  } catch (err) {
    logWarn(`Unreadable incoming call alert ledger`, err)
    return []
  }
}

export function writeAlertLedger(alerts: IncomingCallAlert[]): IncomingCallAlert[] {
  const bounded = alerts.slice(-MAX_ALERTS)
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bounded))
  } catch (err) {
    logWarn(`Could not save incoming call alert ledger`, err)
  }
  return bounded
}

/** The card to put back after a page load: the newest one still up, if its minute has not run out. */
export function restorableAlert({
  alerts,
  workspaceId,
  now,
  durationMs,
}: {
  alerts: IncomingCallAlert[]
  workspaceId: string
  now: number
  durationMs: number
}): IncomingCallAlert | undefined {
  for (let i = alerts.length - 1; i >= 0; i--) {
    const alert = alerts[i]!
    if (alert.dismissed || alert.call.workspaceId !== workspaceId) continue
    return alert.pinned || now - alert.shownAt < durationMs ? alert : undefined
  }
  return undefined
}
